import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import { Input } from './input';
import { Button } from './button';

/**
 * Searchable Item Select Component
 * A dropdown with search functionality for selecting items
 * 
 * Props:
 * - items: Array of items [{id, part_number, description, category}, ...]
 * - value: Selected item ID
 * - onValueChange: Callback when selection changes
 * - placeholder: Placeholder text (default: "All items")
 * - showCategory: Show category in item display (default: true)
 * - filterByCategory: Filter items by category (optional)
 * - className: Additional CSS classes
 * - disabled: Disable the select
 */
const SearchableItemSelect = ({
  items = [],
  value,
  onValueChange,
  placeholder = "All items",
  showCategory = true,
  filterByCategory = null,
  className = "",
  disabled = false,
  dataTestId = "searchable-item-select"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter items by category if specified
  const categoryFilteredItems = useMemo(() => {
    if (!filterByCategory) return items;
    return items.filter(item => item.category === filterByCategory);
  }, [items, filterByCategory]);

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return categoryFilteredItems;
    
    const query = searchQuery.toLowerCase().trim();
    return categoryFilteredItems.filter(item =>
      item.part_number?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.category?.toLowerCase().includes(query)
    );
  }, [categoryFilteredItems, searchQuery]);

  // Get selected item details
  const selectedItem = useMemo(() => {
    if (!value || value === 'all') return null;
    return items.find(item => item.id === value);
  }, [items, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (itemId) => {
    onValueChange(itemId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onValueChange('all');
    setSearchQuery('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
        data-testid={dataTestId}
      >
        <span className={`truncate ${!selectedItem ? 'text-muted-foreground' : ''}`}>
          {selectedItem 
            ? `${selectedItem.part_number} - ${selectedItem.description?.substring(0, 20)}${selectedItem.description?.length > 20 ? '...' : ''}`
            : placeholder
          }
        </span>
        <div className="flex items-center gap-1">
          {selectedItem && (
            <X 
              className="h-4 w-4 text-slate-400 hover:text-slate-600" 
              onClick={handleClear}
            />
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[300px] rounded-md border bg-white shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search by part number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {/* All Items Option */}
            <div
              onClick={() => handleSelect('all')}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 ${
                (!value || value === 'all') ? 'bg-blue-50 text-blue-700' : ''
              }`}
            >
              {placeholder}
            </div>

            {filteredItems.length === 0 ? (
              <div className="px-3 py-4 text-sm text-center text-slate-500">
                No items found
              </div>
            ) : (
              filteredItems.slice(0, 100).map(item => (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item.id)}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 ${
                    value === item.id ? 'bg-blue-50 text-blue-700' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {showCategory && (
                      <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded text-slate-600">
                        {item.category}
                      </span>
                    )}
                    <span className="font-mono text-blue-600">{item.part_number}</span>
                    <span className="text-slate-600 truncate">{item.description}</span>
                  </div>
                </div>
              ))
            )}
            
            {filteredItems.length > 100 && (
              <div className="px-3 py-2 text-xs text-center text-slate-500 border-t">
                Showing first 100 results. Type to narrow down.
              </div>
            )}
          </div>

          {/* Footer with count */}
          <div className="px-3 py-2 text-xs text-slate-500 border-t bg-slate-50">
            {filteredItems.length} items {searchQuery && `matching "${searchQuery}"`}
          </div>
        </div>
      )}
    </div>
  );
};

export { SearchableItemSelect };
export default SearchableItemSelect;
